import { useEffect, useState, ReactNode } from 'react';
import { getUserRoles } from '../api/roles/api';
import { UserRole } from '../types/domain';
import { RolesContext } from '../contexts/RolesContext';
import { useLocalStorage } from '../hooks';

export const RolesProvider = ({ children }: { children: ReactNode }) => {
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { updateCacheItem, getCacheItem, clearUserCache } = useLocalStorage();

  const fetchUserRoles = async (): Promise<UserRole[]> => {
    const CACHE_KEY = 'user_roles_cache';
    const CACHE_EXPIRY_MINUTES = 10;
    
    try {
      const cachedRoles = getCacheItem<UserRole[]>(CACHE_KEY);
      if (cachedRoles) {
        return cachedRoles;
      }
      
      const freshRoles = await getUserRoles();
      updateCacheItem(CACHE_KEY, freshRoles, CACHE_EXPIRY_MINUTES);
      return freshRoles;
    } catch {
      return [];
    }
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const userRoles = await fetchUserRoles();
        setRoles(userRoles);
      } catch (err) {
        setError("Failed to fetch user roles");
        setRoles([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const hasRole = (role: UserRole): boolean => {
    if (isLoading || error) {
      return false;
    }
    
    return roles.includes(role);
  };

  const clearCache = (): void => {
    clearUserCache();
  };

  return (
    <RolesContext.Provider value={{ roles, hasRole, isLoading, error, clearCache }}>
      {children}
    </RolesContext.Provider>
  );
};
